/**
 * WalkthroughProvider — owns the scripted in-app tour state.
 *
 * Mounted at the app root (alongside CopilotProvider) so the tour survives the
 * startup → project → startup transitions it drives. Holds only { active,
 * stepIndex, busy }; the actual screen changes happen via each step's `before`
 * side-effect (run through the action bridge / tool registry). First-run is
 * tracked in localStorage so the copilot launcher only blinks for new users.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { TOUR_STEPS } from '../../services/walkthrough/tourSteps';

const SEEN_KEY = 'litecab_walkthrough_seen';

const hasSeen = () => {
  try { return window.localStorage.getItem(SEEN_KEY) === 'true'; } catch { return true; }
};
const markSeen = () => {
  try { window.localStorage.setItem(SEEN_KEY, 'true'); } catch { /* ignore */ }
};

const WalkthroughContext = createContext(null);

export const useWalkthrough = () => {
  const ctx = useContext(WalkthroughContext);
  if (!ctx) throw new Error('useWalkthrough must be used within a WalkthroughProvider');
  return ctx;
};

export function WalkthroughProvider({ children }) {
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [busy, setBusy] = useState(false); // a step's `before` side-effect is running
  const [firstRunHint, setFirstRunHint] = useState(() => !hasSeen());
  const runIdRef = useRef(0); // guards against overlapping/stale transitions

  // Run a step's `before` side-effect (best-effort) then land on that step.
  const goToStep = useCallback(async (index) => {
    if (index < 0 || index >= TOUR_STEPS.length) return;
    const runId = ++runIdRef.current;
    const step = TOUR_STEPS[index];
    if (typeof step.before === 'function') {
      setBusy(true);
      try {
        await step.before();
      } catch (err) {
        // Never let a side-effect crash the tour; the bubble degrades gracefully.
        console.warn(`[walkthrough] step "${step.id}" setup failed:`, err?.message || err);
      }
    }
    if (runId !== runIdRef.current) return; // a newer transition superseded this one
    setBusy(false);
    setStepIndex(index);
  }, []);

  const start = useCallback(() => {
    markSeen();
    setFirstRunHint(false);
    setActive(true);
    setStepIndex(0);
    goToStep(0);
  }, [goToStep]);

  const next = useCallback(() => {
    if (busy) return;
    if (stepIndex >= TOUR_STEPS.length - 1) { // last step → finish
      runIdRef.current++;
      setActive(false);
      return;
    }
    goToStep(stepIndex + 1);
  }, [busy, stepIndex, goToStep]);

  const back = useCallback(() => {
    if (busy || stepIndex <= 0) return;
    goToStep(stepIndex - 1);
  }, [busy, stepIndex, goToStep]);

  const skip = useCallback(() => {
    runIdRef.current++; // cancel any in-flight transition
    markSeen();
    setBusy(false);
    setActive(false);
  }, []);

  const dismissHint = useCallback(() => {
    markSeen();
    setFirstRunHint(false);
  }, []);

  const value = {
    active,
    busy,
    stepIndex,
    step: active ? TOUR_STEPS[stepIndex] : null,
    stepCount: TOUR_STEPS.length,
    isLast: stepIndex >= TOUR_STEPS.length - 1,
    isFirst: stepIndex <= 0,
    firstRunHint,
    start,
    next,
    back,
    skip,
    dismissHint,
  };

  return <WalkthroughContext.Provider value={value}>{children}</WalkthroughContext.Provider>;
}

export default WalkthroughProvider;
